// import GitHub's fetch polyfill
// ToDo: We are not using this page, should be removed
import 'whatwg-fetch';
import { get } from 'lodash';

import { uniqueId } from '@/utils/setting-up';
import CustomError from '@/utils/custom-error';
import router from '@/router';

/*
    Vue does not provide a native AJAX client , so we've rolled our own,
    based on the emerging fetch standard: https://fetch.spec.whatwg.org/,
    with a polyfill from GitHub: https://github.github.io/fetch/

    the controller class allows for extending the utility of fetch that can
    be easily rolled back as the standard matures. we've built-out support for
    a timeout value in the options object, which triggers a race condition.

    also included is a mechanism for setting defaults for the request object
    passed to fetch. the current implementation reads from the auth store to
    set Authorization and X-CSRF-TOKEN headers on each request.
*/

/* eslint-disable */
// instantiate the singleton
export const RequestController = new (class {
  constructor() {
    // track active requests by id.
    this.activeReqs = new Set();
    this.baseUrl = process.env.VUE_APP_ACTUAL_API_URL;
  }

  fetch(url, options = {}) {
    try {
      // format the request instance - this should be reusable
      const request = Request && url instanceof Request ? url : this.defaultRequest(url, options);
      // prepare a fetch request
      const sendRequest = () => this.send(request);

      if (options.timeout) {
        // forward a reference to the request
        const timer = () => this.setTimer(options.timeout, request);
        // this is a pretty straightforward timeout rejection pattern
        return this.race([sendRequest(), timer()]);
      }

      return sendRequest();
    } catch (error) {
      return Promise.reject(new CustomError({
        message: `It looks like you are using some not supported characters. Take a look of your credentials.`,
        details: {
          ...error,
        },
      }, error));
    }
  }

  _fetch() {
    // return a reference to window.fetch
    if (window.fetch === undefined) {
      throw new Error('window.fetch is undefined');
    }

    return window.fetch;
  }

  race([request, timer]) {
    const { id } = request;
    // we need to surface the id in case both timeout and abort are used
    return Object.assign(Promise.race([request, timer]), { id });
  }

  send(request) {
    let response = null;
    // clone the outgoing request before it is used
    const recycled = request.clone();
    // get a reference to window.fetch
    const doFetch = this._fetch();
    // grab a unique id
    const id = uniqueId();
    // initiate a fetch request
    const req = doFetch(request)

    .then((res) => {
      response = res;
      // check if we're still tracking this request
      if (res && res.status === 401) {
        throw new CustomError({
          // send some justification
          message: 'Unauthorized',
          status: res.status,
        });
      }

      if (res instanceof Response && !res.ok) {
        throw new CustomError(res, `(${get(res, 'status')}) ${get(res, 'statusText', '')}`);
      }

      if (this.activeReqs.has(id)) {
        // proceed as usual
        return res;
      }
      // signal an aborted request
      throw new CustomError({
        // send some justification
        message: `Aborted request to ${request.url}`,
        // the request is re-usable
        request: recycled,
      });
    })
    .catch((error) => {
      if (error.details && error.details.status === 401
        && error.details.message === 'Unauthorized'
        && get(router, 'currentRoute.name') !== 'logout'
      ){
        router.push({ name: 'logout' }).catch();
        return response;
      } else if (error instanceof TypeError && error.message === 'Failed to fetch') {
        const message = `Please verify your VPN connection. Maybe you need to be connected to the VPN.
          The request to the resource of the data has failed. ${error.message}`;
        throw new CustomError({
          // send some justification
          message,
          // the request is re-usable
          request: recycled,
        }, message);
      } else {
        throw error;
      }
    });
    // track this request id
    this.activeReqs.add(id);
    // amend the promise with an id prop - doesn't alter functionality
    return Object.assign(req, { id, headers: request.headers});
  }

  defaultHeaders(headers) {
    const headersObj = {
      // default to sending JSON
      'Content-Type': 'application/json',
      // default to accepting JSON
      Accept: 'application/json',
      /*
       * Security Headers
       * References:
       * https://content-security-policy.com/
       * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Strict-Transport-Security
       * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Cache-Control
       * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Content-Type-Options
       * https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/X-Frame-Options
       */
      'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
      'Cache-Control': 'no-cache',
      'Content-Security-Policy':
        "default-src 'none'; script-src 'self'; connect-src 'self'; img-src 'self'; style-src 'self';",
      'X-Content-Type-Options': 'nosniff',
      'X-Frame-Options': 'sameorigin',
      'Connection': 'keep-alive',
      'Accept-Encoding': 'gzip, deflate, br',
      // passed headers will take precidence above defaults
      ...headers,
    };

    // This is a solution whenever is needed send files over enctype multipart/form-data in order to...
    // The browser is able to set the proper value automatically in the Content-Type header
    if (get(headers, 'Content-Type') === 'multipart/form-data') {
      delete headersObj['Content-Type'];
    }

    // default headers to include with every request
    const _headers = new Headers(headersObj);

    return _headers;
  }

  defaultRequest(url, options) {
    // default request options - do not include timeout or other additions
    const _options = {
      ...options,
      // fetch defaults to GET, and so do we
      method: options.method || 'GET',
      // set the default headers
      headers: this.defaultHeaders(options.headers),
      // default omits cookies on requests to all domains
      credentials: options.credentials || 'include',
    };

    if (options.body) {
      // don't define a request body if one is not included in options
      _options.body = options.body;
    }

    return new Request(url, _options);
  }

  setTimer(timeout, request) {
    // a simple countdown timer
    // a timed-out request should be caught as a promise rejection
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        reject(new CustomError({
          // send some justification
          message: 'timeout',
          // the request is re-usable
          request,
          // the timeout value
          timeout,
        }));
      }, timeout);
    });
  }
})();

// export as a Vue plugin
export function install(Vue) {
  // make this.$request available to Vue components
  /* eslint no-param-reassign: ["error", { "props": false }] */
  Vue.prototype.$request = RequestController;
}
/* eslint-enable */
